import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * useAvatarUnlock — backend-validated gate for the Custom Avatar Pass.
 *
 * Rules:
 *   - `unlocked` is sourced entirely from the authenticated user's `hasPaid`
 *     flag on the server. No localStorage involved.
 *   - `signedIn` tells the UI whether to surface the Google sign-in flow.
 *   - `buy()` fetches the auto-discovered Stripe Payment Link and redirects.
 *   - After return (via `?stripe_check=1` or `?success=true`), we auto-call
 *     /api/stripe/verify; a manual "Verify now" fallback button is exposed
 *     via `verifyNow()`.
 */
export function useAvatarUnlock() {
  const { user, signIn, verifyPurchase } = useAuth();
  const unlocked = !!(user && user.hasPaid);
  const signedIn = !!user;

  const [verifying, setVerifying] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState(null);
  const [verifyError, setVerifyError] = useState(null);
  const autoTriedRef = useRef(false);

  const verifyNow = useCallback(
    async ({ silent = false } = {}) => {
      if (!user) {
        if (!silent) setVerifyError("Sign in first to verify your purchase.");
        return null;
      }
      setVerifying(true);
      setVerifyError(null);
      setVerifyMessage(null);
      try {
        const res = await verifyPurchase();
        if (res && res.hasPaid) {
          setVerifyMessage(
            res.alreadyPaid
              ? "Your pass is already active."
              : "Purchase verified — uploads unlocked!"
          );
        } else if (!silent) {
          setVerifyError(
            "We couldn't find a completed purchase for your account yet. " +
              "Stripe can take a moment — try again in 10–20 seconds."
          );
        }
        return res;
      } catch (e) {
        if (!silent) setVerifyError(String(e.message || e));
        return null;
      } finally {
        setVerifying(false);
      }
    },
    [user, verifyPurchase]
  );

  // Auto-verify once on return from Stripe (?stripe_check=1 or ?success=true).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!user || unlocked) return;
    if (autoTriedRef.current) return;

    const params = new URLSearchParams(window.location.search);
    const isReturn =
      params.get("stripe_check") === "1" ||
      params.get("success") === "true" ||
      params.get("checkout") === "success" ||
      params.get("status") === "succeeded";
    if (!isReturn) return;

    autoTriedRef.current = true;
    verifyNow({ silent: true });

    // Scrub the markers from the URL.
    ["stripe_check", "success", "checkout", "status"].forEach((k) =>
      params.delete(k)
    );
    const q = params.toString();
    const newUrl =
      window.location.pathname + (q ? "?" + q : "") + window.location.hash;
    try {
      window.history.replaceState({}, "", newUrl);
    } catch (e) {
      /* ignore */
    }
  }, [user, unlocked, verifyNow]);

  const buy = useCallback(async () => {
    if (!API) return;
    if (!user) {
      signIn();
      return;
    }
    try {
      const r = await fetch(`${API}/api/stripe/payment-link`, {
        credentials: "include",
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setVerifyError(j.detail || `HTTP ${r.status}`);
        return;
      }
      const data = await r.json();
      if (data && data.url) {
        window.location.href = data.url;
      } else {
        setVerifyError("No payment link available.");
      }
    } catch (e) {
      setVerifyError(String(e.message || e));
    }
  }, [user, signIn]);

  return {
    unlocked,
    signedIn,
    signIn,
    buy,
    verifyNow,
    verifying,
    verifyMessage,
    verifyError,
  };
}
